<template>
  <div class="form">
    <div class="form__content">
      <IdentityQueueStepCounter />
      <h1 class="extra-large-headline">
        {{ $t('PageTitle.headline.idvPageHeader') }}
      </h1>
      <p class="bodycopy">
        {{ $t('PageSubTitle.paragraph.idvInstructions') }}
      </p>
    </div>
    <SkeleLoader
      v-if="isLoading"
      :lines="15"
      :hasContentImg="false"
      contentIsFullWidth
    />
    <div id="sumsub-websdk" v-show="!isLoading" />
    <IdentityQueueNext />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useIdentityQueueStore } from '@/stores/identityQueueStore';

import useScriptTagSetup from '@/composables/helpers/sciptTagSetup.js';
import useApiHelpers from '@/composables/api/helpers';
import useErrorHandling from '@/composables/api/useErrorHandling';
import useModalActions from '@/composables/store/modal/useModalActions';

import SkeleLoader from '@/components/ui/loaders/SkeleLoader.vue';
import IdentityQueueStepCounter from '@/components/layout/progress/IdentityQueueStepCounter.vue';
import IdentityQueueNext from '@/components/layout/progress/IdentityQueueNext.vue';

const route = useRoute();
const router = useRouter();
const identityQueueStore = useIdentityQueueStore();
const { postRequest, getRequest } = useApiHelpers();
const { t } = useI18n();
const { toggleActionModal } = useModalActions();

const { setupScriptTag } = useScriptTagSetup();

const DELAY = 5000;
const SUMSUB_JS_SDK_URL = 'https://static.sumsub.com/idensic/static/sns-websdk-builder.js';
const SUPPORT_EMAIL = 'support@banxa.com';

let token = '';
let sumsubApiUrl = '';
let hasSubmitted = false;
let timeout = null;

const snsWebSdkInstance = ref(null);
const isLoading = ref(true);
const modalOptions = ref({ title: '', text: '' });
const { handleErrorInSpaFlow } = useErrorHandling();

async function getTokenResponse(response) {
  token = response.data.token;
  sumsubApiUrl = response.data.apiUrl;
}

async function getSumsubToken() {
  await postRequest('/sumsub/kyc/token', {}, getTokenResponse);
}

const handleSumsubRecollection = async () => {
  identityQueueStore.updateToNextRoute(route);
  identityQueueStore.hasSubmittedToSumsub = true;
  identityQueueStore.fetchNextIdentityRoute(route).then(async (nextRoute) => {
    router.push(nextRoute);
  });
};

const showModalOnError = (errorMsg) => {
  modalOptions.value = {
    title: t('ActionModal.title.sumsubError'),
    text: `${errorMsg}`,
  };
  toggleActionModal(true, modalOptions.value);
};

function launchWebSdk(accessToken) {
  snsWebSdkInstance.value = snsWebSdk
    .init(accessToken, () => accessToken)
    .withBaseUrl(sumsubApiUrl)
    .withConf({
      lang: 'en',
      onMessage: (type, payload) => {
        console.log('WebSDK onMessage', type, payload);
      },
      uiConf: {
        customCssStr:
          ':root {\n  --black: #000000;\n   --grey: #F5F5F5;\n  --grey-darker: #B2B2B2;\n  --border-color: #DBDBDB;\n}\n\np {\n  color: var(--black);\n  font-size: 16px;\n  line-height: 24px;\n}\n\nsection {\n  margin: 40px auto;\n}\n\ninput {\n  color: var(--black);\n  font-weight: 600;\n  outline: none;\n}\n\nsection.content {\n  background-color: var(--grey);\n  color: var(--black);\n  padding: 40px 40px 16px;\n  box-shadow: none;\n  border-radius: 6px;\n}\n\nbutton.submit,\nbutton.back {\n  text-transform: capitalize;\n  border-radius: 6px;\n  height: 48px;\n  padding: 0 30px;\n  font-size: 16px;\n  background-image: none !important;\n  transform: none !important;\n  box-shadow: none !important;\n  transition: all 0.2s linear;\n}\n\nbutton.submit {\n  min-width: 132px;\n  background: none;\n  background-color: var(--black);\n}\n\n.round-icon {\n  background-color: var(--black) !important;\n  background-image: none !important;\n}',
      },
      onError: (error) => {
        console.error('WebSDK onError', error);
        showModalOnError(error);
      },
    })
    .withOptions({ addViewportTag: false, adaptIframeHeight: true })
    .on('idCheck.applicantStatus', async (payload) => {
      const { reviewStatus, reviewResult } = payload;

      const handleGreenStatus = () => {
        hasSubmitted = true;
        postRequest('/documents/sumsub', payload, handleSumsubRecollection);
      };

      const handleRedStatus = () => {
        if (reviewResult?.reviewRejectType !== 'RETRY') {
          handleErrorInSpaFlow(
            227,
            t('Sumsub.error.finalRejection', { supportEmail: SUPPORT_EMAIL }),
          );
        }
      };

      if (reviewStatus === 'completed') {
        if (reviewResult?.reviewAnswer === 'GREEN') {
          handleGreenStatus();
        } else if (reviewResult?.reviewAnswer === 'RED') {
          handleRedStatus();
        }
        return;
      }

      if (reviewStatus === 'pending') {
        timeout = setTimeout(() => {
          if (!hasSubmitted) {
            handleGreenStatus();
          }
        }, DELAY);
      }
    })
    .on('idCheck.onInitialized', (payload) => {
      isLoading.value = false;
    })
    .on('idCheck.onError', (error) => {
      showModalOnError(error.error);
    })
    .on('onError', (error, payload) => {
      const { msg } = payload;
      console.error(error, payload);
      showModalOnError(msg);
    })
    .onMessage((type, payload) => {
      const { msg } = payload;
      if (msg && type === 'idCheck.onUploadError') showModalOnError(msg);
    })
    .build();
  snsWebSdkInstance.value.launch('#sumsub-websdk');
}

onMounted(async () => {
  await getSumsubToken();
  setupScriptTag(SUMSUB_JS_SDK_URL).then(() => launchWebSdk(token));
});

onUnmounted(() => {
  if (timeout) {
    clearTimeout(timeout);
  }
  identityQueueStore.hasSubmittedToSumsub = false;
});
</script>
<style lang="scss" scoped>
.form {
  .bodycopy {
    margin-bottom: 1rem;
    @include breakpoint($min-m) {
      margin-bottom: 1.5rem;
    }
  }
  &__selfie-instructions {
    list-style: unset;
    padding-inline: 1.5rem;

    li {
      font-size: $font-body;
      line-height: 1.5;
    }
  }
}
</style>
